"use client"

import { useRef, type MouseEvent } from "react"

import { useTranslations } from "next-intl"

import { useReferralShare } from "@/features/referrals/hooks/use-referral-share"
import { facebookShareUrl, productShareLink, whatsappShareUrl } from "@/features/referrals/share"
import { renderStoryImage, type StoryCopy } from "@/features/referrals/story-image"
import type { ReferralShare } from "@/platform/api/referrals"
import type { ShareChannel } from "@/platform/bridge/types"

const MAX_STORY_IMAGES = 3

const outgoing = (share: ReferralShare, productLine: ((url: string) => string) | null) => {
  if (!productLine) return { link: share.share_url, message: share.share_message }
  const link = productShareLink(share.share_url, window.location.pathname, window.location.search)
  const line = productLine(link)
  const message = share.share_message.includes(share.share_url) ? share.share_message.replaceAll(share.share_url, line) : `${share.share_message}\n\n${line}`
  return { link, message }
}

interface ShareActionsInput {
  share: ReferralShare | null
  imageUrls: readonly string[]
  discount: string
  productTitle?: string
}

export const useReferralShareActions = ({ share, imageUrls, discount, productTitle }: ShareActionsInput) => {
  const t = useTranslations("referrals")
  const { feedback, copy, share: shareText, shareStory, nativeShare } = useReferralShare()
  const story = useRef<{ code: string; image: Promise<Blob> } | null>(null)
  const outbound = share ? outgoing(share, productTitle ? (url) => t("productShareLine", { product: productTitle, url }) : null) : null

  const storyCopy = (code: string): StoryCopy => ({
    headlineLead: t("story.headlineLead"),
    headlineEmphasis: t("story.headlineEmphasis"),
    offer: t("story.offer", { discount }),
    stickerHint: t("story.stickerHint"),
    codeLine: t("story.codeLine", { code }),
  })

  const storyImage = (code: string) => {
    if (story.current?.code !== code) {
      const image = renderStoryImage(imageUrls, storyCopy(code))
      story.current = { code, image }
      image.catch(() => {
        if (story.current?.image === image) story.current = null
      })
    }
    return story.current.image
  }

  const inApp = (target: ShareChannel) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (!nativeShare || !outbound) return
    event.preventDefault()
    void shareText(outbound.message, outbound.link, target)
  }

  return {
    feedback,
    ready: outbound !== null,
    copyCode: () => share && void copy("code", share.code),
    copyLink: () => outbound && void copy("link", outbound.link),
    shareAny: () => outbound && void shareText(outbound.message, outbound.link),
    prepareStory: () => share && void storyImage(share.code).catch(() => undefined),
    shareToStory: () => {
      if (!share || !outbound) return
      void shareStory({ code: share.code, shareUrl: outbound.link, imageUrls: imageUrls.slice(0, MAX_STORY_IMAGES) }, () => storyImage(share.code))
    },
    shareDirect: () => outbound && void shareText(outbound.message, outbound.link, "instagram_direct"),
    whatsapp: { href: outbound ? whatsappShareUrl(outbound.message) : null, onClick: inApp("whatsapp") },
    facebook: { href: outbound ? facebookShareUrl(outbound.link) : null, onClick: inApp("facebook") },
  }
}

export type ReferralShareActions = ReturnType<typeof useReferralShareActions>
